import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { signInAdmin } from '../firebase/auth';

const AdminModal = ({ showModal, setShowModal, onLogin }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use Firebase Authentication
      const result = await signInAdmin(credentials.email, credentials.password);
      
      if (result.success) {
        // Call parent onLogin function with success
        onLogin({ username: credentials.email, password: credentials.password });
        setCredentials({ email: '', password: '' });
      } else {
        // Display Firebase error
        if (result.error.includes('user-not-found')) {
          setError('No account found with this email');
        } else if (result.error.includes('wrong-password')) {
          setError('Incorrect password');
        } else if (result.error.includes('invalid-email')) {
          setError('Invalid email format');
        } else if (result.error.includes('too-many-requests')) {
          setError('Too many failed attempts. Try again later');
        } else {
          setError('Login failed. Please check your credentials');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setCredentials({ email: '', password: '' });
    setError('');
    setShowPassword(false);
  };

  if (!showModal) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-login-title"
        className="modal-panel max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={handleClose} className="icon-btn absolute right-4 top-4" aria-label="Close">
          <X size={18} strokeWidth={1.75} />
        </button>

        <h2 id="admin-login-title" className="text-xl font-semibold text-slate-50">Admin Login</h2>
        <p className="mt-2 text-sm text-slate-400">Enter your credentials to access admin features</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          <div>
            <label htmlFor="admin-email" className="field-label">Email address</label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={credentials.email}
              onChange={(e) => setCredentials({...credentials, email: e.target.value})}
              placeholder="Email address"
              required
              disabled={isLoading}
              className="field"
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="field-label">Password</label>
            <div className="relative">
              <input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                placeholder="Password"
                required
                disabled={isLoading}
                className="field pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:text-slate-100"
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
              </button>
            </div>
          </div>

          {error && <p role="alert" className="form-alert">{error}</p>}

          <button
            type="submit"
            disabled={isLoading || !credentials.email || !credentials.password}
            className="btn-primary w-full"
          >
            {isLoading ? (
              <>
                <span className="h-4 w-4 rounded-full border-2 border-slate-950/30 border-t-slate-950 motion-safe:animate-spin" aria-hidden="true" />
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminModal;